import { POSTMESSAGE_TARGET_ORIGIN_WILDCARD } from '../Constants'

// A document with no (or an opaque) referrer reports its origin as this literal.
const OPAQUE_ORIGIN: string = 'null'

/**
 * Parses the origin out of an absolute URL, ignoring anything that is not a usable origin.
 *
 * @param url - Absolute URL to read the origin from. May be null, undefined or unparseable.
 * @returns {Definable<string>} The origin, or undefined when it cannot be determined.
 */
const getOriginFromUrl = (url: Nullable<string>): Definable<string> => {
    if (!url) return undefined

    try {
        const { origin }: URL = new URL(url)

        // Opaque origins (about:blank, data: URLs) cannot receive a targeted postMessage.
        return origin && origin !== OPAQUE_ORIGIN ? origin : undefined
    } catch {
        // Not an absolute URL - treat as undeterminable rather than throwing.
        return undefined
    }
}

/**
 * <p>Resolves the {@code targetOrigin} to use when posting the report-created handshake back to
 * the new Fusion UI that embedded this page in an iframe.
 *
 * <p>The origin of the window actually embedding us is preferred over the configured new-UI URL
 * from {@code new-ui.yaml}: that setting is per-environment and drifts, and a deployment served on
 * a hostname other than the configured one made the browser silently discard the handshake,
 * leaving the create-report spinner stuck forever. The configured URL is kept only as a fallback
 * for browsers that expose neither {@code ancestorOrigins} nor a referrer.
 *
 * @param configuredNewUiUrl - The {@code url} value from the new-UI generic config. May be null.
 * @returns {string} The origin to post to, or {@code *} when no origin can be determined.
 */
export const resolveFusionParentOrigin = (configuredNewUiUrl: Nullable<string>): string => {
    // Chromium/WebKit expose the embedding chain directly - index 0 is the immediate parent.
    const ancestorOrigins: Definable<DOMStringList> = window.location.ancestorOrigins
    const parentOrigin: Definable<string> = ancestorOrigins?.length ? ancestorOrigins[0] : undefined

    return (
        getOriginFromUrl(parentOrigin) ??
        // Firefox has no ancestorOrigins; for an iframe the referrer is the embedding page.
        getOriginFromUrl(document.referrer) ??
        getOriginFromUrl(configuredNewUiUrl) ??
        POSTMESSAGE_TARGET_ORIGIN_WILDCARD
    )
}
